import React, { useCallback, useMemo } from 'react';

import { css } from '@emotion/css';

import { AppEvents, GrafanaTheme2 } from '@grafana/data';
import { t } from '@grafana/i18n';
import { getAppEvents, locationService, reportInteraction, usePluginComponent } from '@grafana/runtime';
import { SceneComponentProps, sceneGraph, SceneObjectBase, SceneObjectState } from '@grafana/scenes';
import { ToolbarButton, useStyles2 } from '@grafana/ui';

import { LoadSearchModal } from './LoadSearchModal';
import { IndexScene } from 'Components/IndexScene/IndexScene';
import { contextToLink } from 'services/extensions/links';
import { LokiQuery } from 'services/lokiQuery';
import { isQueryLibrarySupported, OpenQueryLibraryComponentProps, useHasSavedSearches } from 'services/saveSearch';
import { getDataSourceVariable } from 'services/variableGetters';

export interface LoadSearchSceneState extends SceneObjectState {
  dsName: string;
  dsUid: string;
  isOpen: boolean;
}
export class LoadSearchScene extends SceneObjectBase<LoadSearchSceneState> {
  constructor(state: Partial<LoadSearchSceneState> = {}) {
    super({
      dsUid: '',
      dsName: '',
      isOpen: false,
      ...state,
    });

    this.addActivationHandler(this.onActivate);
  }

  onActivate = () => {
    this.setState({
      dsUid: getDataSourceVariable(this).getValue().toString(),
      dsName: getDataSourceVariable(this).state.text.toString(),
    });

    this._subs.add(
      getDataSourceVariable(this).subscribeToState((newState) => {
        this.setState({
          dsUid: newState.value.toString(),
          dsName: getDataSourceVariable(this).state.text.toString(),
        });
      })
    );
  };

  toggleOpen = () => {
    this.setState({
      isOpen: true,
    });
  };

  toggleClosed = () => {
    this.setState({
      isOpen: false,
    });
  };

  static Component = ({ model }: SceneComponentProps<LoadSearchScene>) => {
    const { dsName, dsUid, isOpen } = model.useState();
    const styles = useStyles2(getStyles);
    const hasSavedSearches = useHasSavedSearches(dsUid);

    const { component: OpenQueryLibraryComponent, isLoading: isLoadingExposedComponent } =
      usePluginComponent<OpenQueryLibraryComponentProps>('grafana/query-library-context/v1');

    const indexScene = useMemo(() => sceneGraph.getAncestor(model, IndexScene), [model]);

    const fallbackComponent = useMemo(
      () => (
        <>
          <ToolbarButton
            icon="folder-open"
            variant="canvas"
            disabled={!hasSavedSearches}
            onClick={model.toggleOpen}
            className={styles.button}
            tooltip={
              hasSavedSearches
                ? t('components.saved-searches.load-search-button.button-tooltip', 'Load saved search')
                : t('components.saved-searches.load-search-button.button-no-search-tooltip', 'No saved searches to load')
            }
          />
          {isOpen && <LoadSearchModal sceneRef={model} onClose={model.toggleClosed} />}
        </>
      ),
      [hasSavedSearches, isOpen, model, styles.button]
    );

    const onSelectQuery = useCallback(
      (query: LokiQuery) => {
        const appEvents = getAppEvents();

        if (query.datasource?.type !== 'loki') {
          appEvents.publish({
            payload: [t('components.saved-searches.load-search-scene.load-type-error', 'Please select a Loki query.')],
            type: AppEvents.alertError.name,
          });
          return;
        }

        const link =
          contextToLink({
            targets: [
              {
                refId: 'A',
                datasource: {
                  uid: query.datasource?.uid,
                  type: 'loki',
                },
                // @ts-expect-error
                expr: query.expr,
              },
            ],
            timeRange: sceneGraph.getTimeRange(indexScene).state.value.raw,
          })?.path ?? '';

        if (link) {
          locationService.push(link);
          reportInteraction('grafana_logs_app_load_search_saved_query_loaded');
        } else {
          appEvents.publish({
            payload: [t('components.saved-searches.load-search-scene.load-error', 'Could not generate a link.')],
            type: AppEvents.alertError.name,
          });
        }
      },
      [indexScene]
    );

    if (indexScene.state.embedded) {
      return null;
    }

    if (!isQueryLibrarySupported()) {
      return fallbackComponent;
    } else if (isLoadingExposedComponent || !OpenQueryLibraryComponent) {
      return null;
    }

    return (
      <OpenQueryLibraryComponent
        className={styles.button}
        context="drilldown"
        datasourceFilters={[dsName]}
        icon="folder-open"
        onSelectQuery={onSelectQuery}
        tooltip={t('components.saved-searches.load-search-button.saved-query-button-tooltip', 'Load saved query')}
      />
    );
  };
}

const getStyles = (theme: GrafanaTheme2) => ({
  button: css({
    [theme.breakpoints.down('lg')]: {
      alignSelf: 'flex-start',
    },
    alignSelf: 'flex-end',
  }),
});
